import { Button, useToast } from "@design";
import { useMutation } from "@tanstack/react-query";
import { Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { errorMessage, removeSpaceAccess } from "@/api";
import { Text } from "@/components/Text";

/** A space whose members can use this item (given through the space, MHS-766). */
export interface SpaceGrant {
  spaceUuid: string;
  spaceName: string;
  itemUuid: string;
  role: string;
  grantedBy?: string | null;
}

/**
 * On an item's Access tab, for its admins: spaces whose members can use it because someone
 * gave the space access. Everyone in those spaces can use it, including people who join later,
 * so its admins see them here and can take the access back.
 */
export function SpaceGrantsSection({
  grants,
  onChanged,
}: Readonly<{ grants: SpaceGrant[]; onChanged: () => Promise<unknown> }>) {
  const { t } = useTranslation("build", { keyPrefix: "access.spaces" });
  const toast = useToast();
  const remove = useMutation({
    mutationFn: (grant: SpaceGrant) =>
      removeSpaceAccess({ path: { space_uuid: grant.spaceUuid, resource_uuid: grant.itemUuid }, throwOnError: true }),
    onSuccess: async (_, grant) => {
      await onChanged();
      toast.success(t("removed", { space: grant.spaceName }));
    },
    onError: (error) => toast.error(errorMessage(error, t("removeFailed"))),
  });
  if (grants.length === 0) return null;
  return (
    <section aria-label={t("title")} className="flex flex-col gap-3 border-t border-border-subtle pt-4">
      <div>
        <Text variant="body">{t("title")}</Text>
        <Text variant="caption" tone="tertiary">
          {t("hint")}
        </Text>
      </div>
      <ul className="flex flex-col divide-y divide-border-subtle">
        {grants.map((grant) => (
          <li key={grant.itemUuid} className="flex items-center gap-3 py-2">
            <Users size={16} aria-hidden className="shrink-0 text-content-tertiary" />
            <div className="min-w-0 flex-1">
              <Text variant="body-sm" className="truncate">
                {grant.spaceName}
              </Text>
              <Text variant="caption" tone="tertiary">
                {grant.grantedBy ? t("givenBy", { name: grant.grantedBy }) : t("members")}
              </Text>
            </div>
            <Button
              variant="secondary"
              size="sm"
              aria-label={t("remove", { space: grant.spaceName })}
              loading={remove.isPending && remove.variables?.itemUuid === grant.itemUuid}
              onClick={() => remove.mutate(grant)}
            >
              {t("removeShort")}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
